/**
 * F23 (Wave 2C) — admin commission statement detail page.
 *
 * Route: `/commission-statements/:statementId`
 * Permission: `payments:read` (super_admin + support + finance).
 *
 * Header with venue + period + status. Status banner reflects the lifecycle
 * (draft / issued / settled / void). KPIs across 4 cards: Records / Gross
 * paid / Commission / Net (gross − commission).
 *
 * Actions row (each gated server-side; the buttons are visible to
 * `commission_statements:settle` holders — super_admin + finance):
 *   - Mark settled (only when status=issued)
 *   - Void          (only when status ∈ {issued, settled})
 *   - Manual issue  (only when status=draft)
 *
 * Records table at the bottom paginates client-side over the embedded
 * `records[]` payload returned by the server. Each row links to the
 * F17 admin booking detail page.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Chip,
  CurrencyDisplay,
  EmptyState,
  InvoiceStatusBadge,
  type InvoiceStatus,
  Pagination,
  Skeleton,
  Stat,
  StatusChip,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type {
  CommissionRecordDetail,
  CommissionStatementStatus,
  MarkStatementSettledInput,
  VoidStatementInput,
} from '@bravemove/types';
import {
  adminCommissionApi,
  type AdminCommissionStatementDetailResponse,
} from '../../api/commission';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { MarkSettledDialog } from '../../components/commission/MarkSettledDialog';
import { VoidStatementDialog } from '../../components/commission/VoidStatementDialog';
import { Dialog } from '@bravemove/ui-web';

const PAGE_SIZE = 25;

/**
 * Map the F23 statement status onto the `InvoiceStatusBadge` variants. `settled`
 * reads as `paid`; `void` has no badge variant and falls back to a `StatusChip`.
 */
function asInvoiceStatus(status: CommissionStatementStatus): InvoiceStatus | null {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'issued':
      return 'issued';
    case 'settled':
      return 'paid';
    default:
      return null; // 'void' — no badge variant
  }
}

function formatPeriod(periodStart: string, locale: string): string {
  try {
    const d = new Date(periodStart);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      timeZone: 'Asia/Beirut',
    }).format(d);
  } catch {
    return periodStart;
  }
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatBookingDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatRate(bp: number): string {
  return `${(bp / 100).toFixed(2)}%`;
}

export function CommissionStatementDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const { statementId } = useParams<{ readonly statementId: string }>();

  const canSettle = auth.adminUser?.permissions?.includes('commission_statements:settle') ?? false;

  const [data, setData] = useState<AdminCommissionStatementDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recordPage, setRecordPage] = useState(1);

  const [settleOpen, setSettleOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!statementId) return;
    setLoading(true);
    setError(null);
    try {
      // CST-005 — drive the records pagination SERVER-SIDE: the backend
      // returns at most one page of attached records (default 100, max 200)
      // plus the `recordsPagination` sidecar carrying the real total.
      const res = await adminCommissionApi.statementDetail(statementId, {
        recordsPage: recordPage,
        recordsPerPage: PAGE_SIZE,
      });
      setData(res);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [statementId, recordPage, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const records: readonly CommissionRecordDetail[] = data?.records ?? [];
  // CST-005 — the real total lives in the server's recordsPagination sidecar,
  // NOT in the length of the returned slice.
  const recordTotal = data?.recordsPagination.total ?? records.length;
  const recordPageCount = Math.max(1, Math.ceil(recordTotal / PAGE_SIZE));

  const onMarkSettled = async (input: MarkStatementSettledInput) => {
    if (!statementId) return;
    setActionBusy(true);
    try {
      await adminCommissionApi.markStatementSettled(statementId, input);
      toast.push({
        tone: 'success',
        title: t('admin:commission.toast.statement_settled', {
          defaultValue: 'Statement marked settled.',
        }),
      });
      void fetchDetail();
    } catch (err) {
      // CST-008 — surface the failure (409 state conflict / 429 / network)
      // instead of an unhandled rejection, mirroring the onIssue pattern.
      // Re-throw so MarkSettledDialog stays open on failure.
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
      throw err;
    } finally {
      setActionBusy(false);
    }
  };

  const onVoid = async (input: VoidStatementInput) => {
    if (!statementId) return;
    setActionBusy(true);
    try {
      await adminCommissionApi.voidStatement(statementId, input);
      toast.push({
        tone: 'success',
        title: t('admin:commission.toast.statement_voided', {
          defaultValue: 'Statement voided.',
        }),
      });
      void fetchDetail();
    } catch (err) {
      // CST-008 — see onMarkSettled. Re-throw so VoidStatementDialog stays open.
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
      throw err;
    } finally {
      setActionBusy(false);
    }
  };

  const onIssue = async () => {
    if (!statementId) return;
    setActionBusy(true);
    try {
      await adminCommissionApi.issueStatement(statementId);
      toast.push({
        tone: 'success',
        title: t('admin:commission.toast.statement_issued', {
          defaultValue: 'Statement issued.',
        }),
      });
      setIssueOpen(false);
      void fetchDetail();
    } catch (err) {
      toast.push({
        tone: 'danger',
        title: translateApiError(err, t),
      });
    } finally {
      setActionBusy(false);
    }
  };

  const recordColumns = useMemo<readonly TableColumn<CommissionRecordDetail>[]>(
    () => [
      {
        key: 'date',
        header: t('admin:commission.table.records.column.date', { defaultValue: 'Date' }),
        cell: (r) => (
          <span className="text-[12px] text-ink-2">
            {formatBookingDate(r.paymentMarkedAt, locale)}
          </span>
        ),
      },
      {
        key: 'booking',
        header: t('admin:commission.table.records.column.booking', { defaultValue: 'Booking' }),
        cell: (r) => (
          <a
            className="font-mono text-[11.5px] text-accent hover:underline"
            href={`/bookings/${r.bookingId}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/bookings/${r.bookingId}`);
            }}
            data-testid={`commission-record-booking-link-${r.id}`}
          >
            {r.bookingId.slice(0, 8)}…
          </a>
        ),
      },
      {
        key: 'sport',
        header: t('admin:commission.table.records.column.sport', { defaultValue: 'Sport' }),
        cell: (r) => <span className="text-[12px] text-ink-2">{r.booking.sportName}</span>,
      },
      {
        key: 'subVenue',
        header: t('admin:commission.table.records.column.sub_venue', {
          defaultValue: 'Sub-venue',
        }),
        cell: (r) => (
          <span className="text-[12px] text-ink-2">{r.booking.subVenueName ?? '—'}</span>
        ),
      },
      {
        key: 'base',
        header: t('admin:commission.table.records.column.base', { defaultValue: 'Base amount' }),
        align: 'end',
        cell: (r) => (
          <CurrencyDisplay amount={r.baseAmount} currency={r.baseCurrency} locale={locale} />
        ),
      },
      {
        key: 'rate',
        header: t('admin:commission.table.records.column.rate', { defaultValue: 'Rate' }),
        align: 'end',
        cell: (r) => (
          <span className="font-mono text-[12px] text-ink-2 tabular-nums">
            {formatRate(r.rateBasisPoints)}
          </span>
        ),
      },
      {
        key: 'commission',
        header: t('admin:commission.table.records.column.commission', {
          defaultValue: 'Commission',
        }),
        align: 'end',
        cell: (r) => (
          <CurrencyDisplay amount={r.commissionAmount} currency={r.baseCurrency} locale={locale} />
        ),
      },
      {
        key: 'kind',
        header: t('admin:commission.table.records.column.kind', { defaultValue: 'Kind' }),
        cell: (r) => (
          <Badge tone={r.kind === 'accrual' ? 'neutral' : 'danger'} size="sm">
            {r.kind === 'accrual'
              ? t('admin:commission.chip.accrual', { defaultValue: 'Accrual' })
              : t('admin:commission.chip.reversal', { defaultValue: 'Reversal' })}
          </Badge>
        ),
      },
    ],
    [t, locale, navigate],
  );

  if (!statementId) return null;

  if (loading) {
    return (
      <div className="flex flex-col gap-4" data-testid="commission-statement-detail-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div data-testid="commission-statement-detail-error">
        <Banner tone="danger">
          {error ??
            t('admin:commission.toast.error_generic', {
              defaultValue: 'Something went wrong. Please try again.',
            })}
        </Banner>
      </div>
    );
  }

  // CST-012 — trainer-source statements carry venueName='—'; the server now
  // resolves the trainer's display name so the title + the Void
  // type-to-confirm get a human-typable label.
  const entityName =
    data.trainerDisplayName && data.trainerDisplayName.trim().length > 0
      ? data.trainerDisplayName
      : data.venueName;

  const titleText = `${t('admin:commission.page.statement_detail.title_template', {
    venueName: entityName,
    period: formatPeriod(data.periodStart, locale),
    defaultValue: 'Statement for {venueName}, {period}',
  })}`;

  const net = data.grossPaidAmountSum - data.commissionAmountSum;

  return (
    <div className="space-y-6" data-testid="commission-statement-detail-page">
      <button
        type="button"
        className="text-[12px] text-accent hover:underline"
        onClick={() => navigate('/commission-statements')}
        data-testid="commission-statement-detail-back"
      >
        ←{' '}
        {t('admin:commission.page.statement_detail.back_to_queue', {
          defaultValue: 'Back to statements',
        })}
      </button>

      <div className="space-y-2">
        <h1 className="text-h1 font-bold text-ink" data-testid="commission-statement-detail-title">
          {titleText}
        </h1>
        <div className="flex items-center gap-2">
          {(() => {
            const inv = asInvoiceStatus(data.status);
            return inv ? (
              <InvoiceStatusBadge
                size="md"
                status={inv}
                label={t(`commission:status.${data.status}`, { defaultValue: data.status })}
              />
            ) : (
              <StatusChip
                size="md"
                status="rejected"
                label={t(`commission:status.${data.status}`, { defaultValue: data.status })}
              />
            );
          })()}
          {data.venueSlug ? (
            <Chip data-testid="commission-statement-detail-slug">{data.venueSlug}</Chip>
          ) : null}
        </div>
      </div>

      {data.status === 'settled' ? (
        <Banner
          tone="success"
          title={t('admin:commission.page.statement_detail.status_banner.settled_title', {
            defaultValue: 'Settled',
          })}
        >
          {t('admin:commission.page.statement_detail.status_banner.settled_body', {
            settledAt: formatDate(data.settledAt, locale),
            settledByName: data.settledByAdminDisplayName ?? '—',
            reference: data.settledReference ?? '—',
            defaultValue: 'Settled on {settledAt} by {settledByName}. Reference: {reference}.',
          })}
        </Banner>
      ) : data.status === 'void' ? (
        <Banner
          tone="danger"
          title={t('admin:commission.page.statement_detail.status_banner.void_title', {
            defaultValue: 'Voided',
          })}
        >
          {t('admin:commission.page.statement_detail.status_banner.void_body', {
            voidedAt: formatDate(data.voidedAt, locale),
            reason: data.voidedReason ?? '—',
            defaultValue: 'Voided on {voidedAt}. Reason: {reason}.',
          })}
        </Banner>
      ) : data.status === 'draft' ? (
        <Banner
          tone="info"
          title={t('admin:commission.page.statement_detail.status_banner.draft_title', {
            defaultValue: 'Draft',
          })}
        >
          {t('admin:commission.page.statement_detail.status_banner.draft_body', {
            defaultValue:
              'Open accrual period. Records are still being attached. Use Manual issue to close early.',
          })}
        </Banner>
      ) : (
        <Banner
          tone="info"
          title={t('admin:commission.page.statement_detail.status_banner.issued_title', {
            defaultValue: 'Issued',
          })}
        >
          {t('admin:commission.page.statement_detail.status_banner.issued_body', {
            issuedAt: formatDate(data.issuedAt, locale),
            issuedVia: data.issuedVia
              ? t(`admin:commission.page.statement_detail.issued_via.${data.issuedVia}`, {
                  defaultValue: data.issuedVia,
                })
              : '—',
            defaultValue: 'Closed on {issuedAt} via {issuedVia}. Awaiting venue settlement.',
          })}
        </Banner>
      )}

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
        data-testid="commission-statement-detail-kpis"
      >
        <Card>
          <Stat
            label={t('admin:commission.page.statement_detail.kpis.records', {
              defaultValue: 'Records',
            })}
            value={String(data.recordsCount)}
          />
        </Card>
        <Card>
          <Stat
            label={t('admin:commission.page.statement_detail.kpis.gross_paid', {
              defaultValue: 'Gross paid',
            })}
            value={
              <CurrencyDisplay
                amount={data.grossPaidAmountSum}
                currency="USD"
                locale={locale}
                size="lg"
              />
            }
          />
        </Card>
        <Card>
          <Stat
            label={t('admin:commission.page.statement_detail.kpis.commission', {
              defaultValue: 'Commission',
            })}
            value={
              <CurrencyDisplay
                amount={data.commissionAmountSum}
                currency="USD"
                locale={locale}
                size="lg"
              />
            }
          />
        </Card>
        <Card>
          <Stat
            label={t('admin:commission.page.statement_detail.kpis.net', {
              defaultValue: 'Net to venue',
            })}
            value={<CurrencyDisplay amount={net} currency="USD" locale={locale} size="lg" />}
          />
        </Card>
      </div>

      {canSettle ? (
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="commission-statement-detail-actions"
        >
          {data.status === 'issued' ? (
            <Button
              variant="primary"
              onClick={() => setSettleOpen(true)}
              data-testid="commission-statement-detail-settle-cta"
            >
              {t('admin:commission.action.settle', { defaultValue: 'Mark settled' })}
            </Button>
          ) : null}
          {data.status === 'draft' ? (
            <Button
              variant="primary"
              onClick={() => setIssueOpen(true)}
              data-testid="commission-statement-detail-issue-cta"
            >
              {t('admin:commission.action.issue', { defaultValue: 'Issue now' })}
            </Button>
          ) : null}
          {data.status === 'issued' || data.status === 'settled' ? (
            <Button
              variant="destructive"
              onClick={() => setVoidOpen(true)}
              data-testid="commission-statement-detail-void-cta"
            >
              {t('admin:commission.action.void', { defaultValue: 'Void' })}
            </Button>
          ) : null}
        </div>
      ) : (
        <div data-testid="commission-statement-detail-readonly-banner">
          {/* CST-009 — dedicated keys; the previous borrowed keys rendered
              "Something went wrong. Please try again. / Records" to support. */}
          <Banner
            tone="info"
            title={t('admin:commission.page.statement_detail.readonly_title', {
              defaultValue: 'Read-only view',
            })}
          >
            {t('admin:commission.page.statement_detail.readonly_body', {
              defaultValue:
                'Your role can read this statement but cannot mark it settled, issue it, or void it.',
            })}
          </Banner>
        </div>
      )}

      <div className="space-y-3">
        <h2 className="text-h2 font-bold text-ink">
          {t('admin:commission.page.statement_detail.records_section_title', {
            defaultValue: 'Attached records',
          })}
        </h2>
        {records.length === 0 ? (
          <EmptyState
            title={t('admin:commission.page.statement_detail.no_records', {
              defaultValue: 'This statement has no attached records.',
            })}
          />
        ) : (
          <>
            <Table
              data={records as CommissionRecordDetail[]}
              columns={recordColumns as TableColumn<CommissionRecordDetail>[]}
              rowKey={(r) => r.id}
            />
            {recordPageCount > 1 ? (
              <div className="flex justify-end">
                <Pagination
                  page={recordPage}
                  pageCount={recordPageCount}
                  onChange={setRecordPage}
                />
              </div>
            ) : null}
          </>
        )}
      </div>

      <MarkSettledDialog
        open={settleOpen}
        statementId={statementId}
        onConfirm={onMarkSettled}
        onClose={() => setSettleOpen(false)}
      />
      {data.status === 'issued' || data.status === 'settled' ? (
        <VoidStatementDialog
          open={voidOpen}
          statementId={statementId}
          // CST-012 — trainer statements pass the resolved trainer name so the
          // type-to-confirm asks for a human-typable string (never '—').
          venueName={entityName}
          currentStatus={data.status}
          onConfirm={onVoid}
          onClose={() => setVoidOpen(false)}
        />
      ) : null}
      <Dialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        onConfirm={() => void onIssue()}
        title={t('admin:commission.dialog.issue.title', { defaultValue: 'Issue statement now' })}
        description={t('admin:commission.dialog.issue.description', {
          defaultValue:
            "Close the current draft period and issue the statement immediately. Use only when closing a venue's account before period close.",
        })}
        confirmLabel={t('admin:commission.dialog.issue.confirm', { defaultValue: 'Issue now' })}
        cancelLabel={t('admin:commission.dialog.issue.cancel', { defaultValue: 'Cancel' })}
        loading={actionBusy}
      />
    </div>
  );
}

export default CommissionStatementDetailPage;
